import { useState, useEffect, useCallback, useRef } from 'react';
import type { PipelineState, StageStatus, QueueDiagnostics } from './types';

// Helper logic for statuses now inside mapItemToPipeline
const mapItemToPipeline = (
    auditItem: any,
    activeJob: any,
    progressCache: any
): PipelineState => {
    // 1. Prefer Live Progress (Cache or Job) -> Audit Progress -> Failure
    const progress = activeJob?.progress || progressCache?.progress || auditItem?.progress;

    // 2. Use Backend Derived Status if available
    const backendStatus = auditItem?.derivedStatus?.status;
    const backendReason = auditItem?.derivedStatus?.reason;

    // 3. Determine Overall Status
    let overall: StageStatus = 'unknown';

    if (activeJob) {
        overall = 'running';
        if (activeJob.status === 'queued') overall = 'queued';
    } else if (backendStatus) {
        // Map backend derived status to StageStatus
        if (backendStatus === 'completed') overall = 'ok';
        else if (backendStatus === 'failed') overall = 'broken';
        else if (backendStatus === 'processing') overall = 'incomplete';
        else if (backendStatus === 'missing_assets') overall = 'missing';
        else if (backendStatus === 'ready') overall = 'ready';
        else overall = 'unknown';
    } else {
        // Legacy Fallback
        if (progress?.status === 'completed' || progress?.status === 'done') overall = 'ok';
        else if (progress?.status === 'failed') overall = 'broken';
    }

    // 4. Details
    const textStatus: StageStatus = 'ok'; // assume source text exists in manifest for now

    return {
        id: auditItem.id,
        title: auditItem.title || auditItem.id,
        overall,
        sourceUrl: auditItem.sourceUrl || auditItem.derivedStatus?.sourceUrl,
        updatedAt: auditItem.updatedAt,
        trimStartSeconds: auditItem.trimStartSeconds, // Passed down from inspect result

        // P2 Data
        health: auditItem.health,
        derivedStatus: auditItem.derivedStatus,

        stages: {
            text: { status: textStatus, details: backendReason || 'Checked' },
            audio: { status: overall, details: backendReason || 'Status derived' },
            art: { status: 'unknown' },
            manifest: { status: 'unknown' }
        },
        activeJob: activeJob ? {
            ...activeJob,
            // Ensure visual consistency
            progress: typeof activeJob.progress === 'number' ? activeJob.progress : auditItem?.derivedStatus?.progress
        } : undefined
    };
};

export const usePipeline = (pollingEnabled: boolean) => {
    // STATE
    // STATE
    const [data, setData] = useState<PipelineState[] | null>(null);
    const [activeJobs, setActiveJobs] = useState<any[]>([]);
    const [diagnostics, setDiagnostics] = useState<QueueDiagnostics | null>(null);
    const [meta, setMeta] = useState<{ brokenCount: number; brokenIds: string[]; deployTag?: string }>({ brokenCount: 0, brokenIds: [] });
    const [dlqEvents, setDlqEvents] = useState<any[]>([]);

    // LOADING STATES
    const [isAuthLoading, setAuthLoading] = useState(true); // Initial blocking load
    const [queueLoading, setQueueLoading] = useState(true);
    const [auditLoading, setAuditLoading] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [auditFailed, setAuditFailed] = useState(false);
    const [auditError, setAuditError] = useState<string | null>(null);

    // REFS (Stable Data / Polling Guards)
    const isPolling = useRef(false);
    const activeJobsRef = useRef<any[]>([]);
    const progressCache = useRef<Map<string, any>>(new Map()); // bookId -> progress data

    // Fetch Token
    const getToken = () => localStorage.getItem('X_ADMIN_TOKEN') || '';

    const adminFetch = useCallback(async (url: string, options?: RequestInit) => {
        const token = getToken();
        if (!token) throw new Error("No Admin Token");
        return fetch(url, {
            ...options,
            headers: {
                ...options?.headers,
                'X-Admin-Token': token,
                'Content-Type': 'application/json'
            }
        });
    }, []);

    // FETCH PROGRESS (Concurrent Limited)
    const fetchProgressBatch = async (runningJobs: any[]) => {
        const LIMIT = 10;
        const candidates = runningJobs.filter(j => j && (j.bookId || j.params?.id));

        // Simple batching (first N for now, could be round-robin later)
        const batch = candidates.slice(0, LIMIT);

        const results = await Promise.all(batch.map(async (job) => {
            const bookId = job.bookId || job.params?.id;
            try {
                // Short timeout to avoid stalling
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);

                const r = await adminFetch(`/api/library/audio/${bookId}/progress.json`, { signal: controller.signal });
                clearTimeout(timeoutId);

                if (r.ok) {
                    const p = await r.json();
                    return { bookId, progress: p };
                }
            } catch (e) { /* ignore 404/mem */ }
            return { bookId, progress: null };
        }));

        // Update Cache
        results.forEach(r => {
            if (r.bookId && r.progress) {
                progressCache.current.set(r.bookId, r.progress);
            }
        });
    };

    const refresh = useCallback(async (mode: 'full' | 'poll' = 'full') => {
        const token = getToken();
        if (!token) {
            setAuthLoading(false);
            setQueueLoading(false);
            if (error !== "No Admin Token") setError("No Admin Token");
            return;
        }

        // Single Flight Check (Poll only)
        if (mode === 'poll' && isPolling.current) return;
        if (mode === 'poll') isPolling.current = true;

        try {
            // 1. CRITICAL DATA (Queue + DLQ) - Always Fetch
            if (mode === 'full') setQueueLoading(true);

            const [pStatus, pDlq] = await Promise.allSettled([
                adminFetch('/api/factory/queue/status'),
                adminFetch('/api/factory/dlq/recent?limit=50&status=open')
            ]);

            // Aux Parsing
            const statusData = pStatus.status === 'fulfilled' && pStatus.value.ok ? await pStatus.value.json() : {};
            const dlqData = pDlq.status === 'fulfilled' && pDlq.value.ok ? await pDlq.value.json() : {};

            if (statusData.status === 401) throw new Error("No Admin Token");

            // 2. Normalize Active Jobs (Strictly from Queue Status)
            const mapJob = (j: any) => ({
                id: j.jobId || j.id, // Explicit Job ID
                bookId: j.bookId || j.params?.id, // Explicit Book ID
                status: j.status || 'queued',
                step: j.step || j.processingState?.phase || '',
                updatedAt: j.updatedAt || j.updated,
                submittedAt: j.submittedAt,
                lastError: j.lastError || null,
                params: j.params || { id: j.bookId || j.params?.id, title: j.title || j.params?.title, author: j.author || j.params?.author }
            });

            // Merge Queued & Running (No Legacy)
            const jobsMap = new Map<string, any>();
            (statusData.queuedPreview || []).forEach((j: any) => { const m = mapJob(j); if (m.id) jobsMap.set(m.id, m); });
            (statusData.runningPreview || []).forEach((j: any) => { const m = mapJob(j); if (m.id) jobsMap.set(m.id, m); });

            const activeList = Array.from(jobsMap.values());
            setActiveJobs(activeList);
            setDiagnostics(statusData);
            setDlqEvents(dlqData.events || []);
            setMeta({
                brokenCount: 0,
                brokenIds: [],
                deployTag: statusData.deployTag
            });

            // PROGRESSIVE: Unblock UI immediately after Queue is loaded
            setAuthLoading(false);
            setQueueLoading(false);
            setError(null);

            // 3. Assemble Pipeline Data (No Deep Inspect per user Phase 2 rule)
            if (mode === 'full') {
                setAuditLoading(true);
                setAuditFailed(false);
                setAuditError(null);

                const bookJobMap = new Map();
                activeList.forEach((j: any) => { if (j.bookId) bookJobMap.set(j.bookId, j); });

                let pipeline: any[] = [];
                try {
                    // Fetch shallow audit which is now extremely cheap (no R2 list ops)
                    const cacheRes = await adminFetch(`/api/factory/audit/latest`);
                    if (cacheRes.ok) {
                        const cacheData = await cacheRes.json();
                        pipeline = (cacheData.auditResults || []).map((r: any) => ({
                            ...r,
                            activeJob: bookJobMap.get(r.id),
                            title: bookJobMap.get(r.id)?.params?.title || r.title || r.id
                        }));
                    }
                } catch (e: any) {
                    console.error("Shallow Audit Fetch Error", e);
                    setAuditFailed(true);
                    setAuditError(e.message || "Network Error");
                }

                // Ensure all active jobs appear even if they aren't in the manifest yet
                const existingIds = new Set(pipeline.map(p => p.id));
                activeList.forEach((j: any) => {
                    if (j.bookId && !existingIds.has(j.bookId)) {
                        pipeline.push({
                            id: j.bookId,
                            title: j.params?.title || j.bookId,
                            updatedAt: j.updatedAt,
                            health: { hasReconcile: false, hasProgress: false, hasPlaylist: false, hasAudio: false, hasCover: false },
                            derivedStatus: { status: 'processing' as const, reason: 'Processing (Not in manifest)', progress: 0 },
                            activeJob: j
                        });
                        existingIds.add(j.bookId);
                    }
                });

                setData(pipeline);
                setAuditLoading(false);
            }

            // If poll mode, re-hydrate existing pipeline with new job status
            if (mode === 'poll' && data && data.length > 0) {
                const bookJobMap = new Map();
                activeList.forEach((j: any) => { if (j.bookId) bookJobMap.set(j.bookId, j); });

                setData(prev => (prev || []).map(item => mapItemToPipeline(item, bookJobMap.get(item.id), progressCache.current)));
            }

        } catch (e: any) {
            console.error(e);
            setError(e.message || "Pipeline Error");
            setAuthLoading(false);
            setQueueLoading(false);
            if (mode === 'full') setAuditLoading(false);
        } finally {
            if (mode === 'poll') isPolling.current = false;
        }
    }, [data, isAuthLoading, adminFetch]);

    // Interval
    useEffect(() => {
        refresh('full'); // Initial Load

        if (!pollingEnabled) return;

        const interval = setInterval(() => {
            if (document.visibilityState === 'visible' && getToken()) refresh('poll');
        }, 60000); // 60s Poll
        return () => clearInterval(interval);
    }, [refresh, pollingEnabled]);

    // ACTIONS
    const runDrain = async () => {
        try {
            const r = await adminFetch('/api/factory/queue/drain', { method: 'POST', body: JSON.stringify({ maxJobs: 1, maxSeconds: 60 }) });
            await refresh();
            return await r.json();
        } catch (e) { alert("Drain failed: " + e); return null; }
    };

    const runRepairCook = async (id: string, params: any) => {
        try {
            const r = await adminFetch('/api/factory/cook', {
                method: 'POST',
                body: JSON.stringify({
                    id,
                    mode: 'full',
                    ...params
                })
            });
            const d = await r.json();

            // Cache for R2 visibility
            if (d.ok && d.id) {
                try {
                    const cache = JSON.parse(localStorage.getItem('factory_v3_state_v1') || '[]');
                    const newItem = {
                        jobId: d.id,
                        bookId: id,
                        title: params.title || id, // Pass title if known
                        author: params.author || 'Unknown',
                        ts: Date.now()
                    };
                    cache.unshift(newItem);
                    localStorage.setItem('factory_v3_state_v1', JSON.stringify(cache));
                } catch (e) { console.warn("Local storage write fail", e); }
            }

            // Auto Kick
            runDrain().catch(console.error);
            refresh(); // Triggers re-read of local storage
            return d;
        } catch (e) {
            alert("Repair failed: " + e);
            throw e;
        }
    };

    const repairStepFull = async (id: string, step: string, sourceUrl?: string) => {
        return runRepairCook(id, { sourceUrl, resolution: sourceUrl ? 'reset' : undefined });
    };

    const reconcileBook = useCallback(async (bookId: string) => {
        try {
            const res = await adminFetch(`/api/factory/reconcile/${bookId}`, { method: 'POST' });
            if (!res.ok) throw new Error("Reconcile failed");
            runDrain().catch(console.error);
            await refresh();
            return await res.json();
        } catch (e) { console.error(e); throw e; }
    }, [adminFetch, refresh]);

    const abandonStale = async (dryRun: boolean) => {
        const r = await adminFetch('/api/factory/queue/abandon_stale_running', { method: 'POST', body: JSON.stringify({ dryRun }) });
        await refresh();
        return await r.json();
    };

    const dumpQueue = async () => {
        const r = await adminFetch('/api/factory/queue/dump', { method: 'GET' });
        return await r.json();
    };

    const kickJob = async (jobId: string) => {
        try {
            const r = await adminFetch(`/api/factory/job/${jobId}/kick`, { method: 'POST' });
            const d = await r.json();
            if (d.ok) {
                // Toast / Feedback
                // alert("Kick queued a retry; it won't interrupt the current running job."); // User requested implicit toast, but alert is disruptive.
                // We will rely on UI update.
                console.log("Kick success:", d);
            }
            // Double Refresh (1s, 5s) to catch state changes
            await refresh();
            setTimeout(refresh, 1000);
            setTimeout(refresh, 5000);
        } catch (e) { alert("Kick failed: " + e); }
    };

    // New Helper: Set Voice
    const setJobParam = async (id: string, param: string, value: any) => {
        // We assume re-cook is the way to set params
        // cook({ id, [param]: value, resolution: 'replace' })
        return runRepairCook(id, { [param]: value, resolution: 'replace' });
    };

    // Pass-throughs
    const fetchDlqRecent = async (limit = 50, status = 'open') => (await adminFetch(`/api/factory/dlq/recent?limit=${limit}&status=${status}`)).json();
    const requeueDlq = async (eventId: string, mode: 'same' | 'repair_scan') => (await adminFetch('/api/factory/dlq/requeue', { method: 'POST', body: JSON.stringify({ eventId, mode }) })).json();
    const abandonDlq = async (eventId: string, reason: string) => (await adminFetch('/api/factory/dlq/abandon', { method: 'POST', body: JSON.stringify({ eventId, reason }) })).json();
    const regenerateCover = async (id: string) => (await adminFetch(`/api/library/cover/${id}/regenerate`, { method: 'POST' })).json();
    const rebuildThumbs = async (id: string) => (await adminFetch(`/api/library/cover/${id}/rebuild`, { method: 'POST' })).json();

    // New Helper: Get Single Job Status (Deep)
    const getJobStatus = async (jobId: string) => {
        try {
            const r = await adminFetch(`/api/factory/job/${jobId}/status`);
            return await r.json();
        } catch (e) { return { ok: false, error: String(e) }; }
    };

    // New Helper: Deep Inspect Single Book
    const inspectBook = useCallback(async (bookId: string, deep = false) => {
        try {
            const res = await adminFetch(`/api/factory/inspect?ids=${bookId}${deep ? '&deep=1' : ''}`);
            if (!res.ok) throw new Error("Inspect failed");
            const json = await res.json();
            const details = json.results?.[bookId];
            if (details) {
                // Update pipeline state locally so UI updates
                setData(prev => (prev || []).map(b => {
                    if (b.id !== bookId) return b;
                    return {
                        ...b,
                        trimStartSeconds: details.offsetSource === 'manifest' ? details.offsetSec : undefined,
                        health: {
                            hasReconcile: details.hasReconcile,
                            hasProgress: details.hasProgress,
                            hasPlaylist: details.hasPlaylist,
                            hasAudio: details.audioCount > 0,
                            hasCover: details.covers.full || details.covers.grid || details.covers.hi || details.covers.master,
                            keys: {
                                playlist: { exists: details.hasPlaylist },
                                progress: { exists: details.hasProgress },
                                reconcile: { exists: details.hasReconcile }
                            }
                        },
                        derivedStatus: {
                            status: details.statusDerived.toLowerCase(),
                            reason: details.progressStatus || `Audio: ${details.audioCount}/${details.totalChunks}`,
                            progress: details.totalChunks > 0 ? (details.chunksCompleted / details.totalChunks) : 0,
                            audioCount: details.audioCount,
                            totalChunks: details.totalChunks,
                            chunksCompleted: details.chunksCompleted,
                            sourceCount: details.sourceCount,
                            offsetSec: details.offsetSec,
                            offsetSource: details.offsetSource,
                            missingChunks: details.missingChunks,
                            covers: details.covers
                        }
                    };
                }));
            }
            return details;
        } catch (e) { console.error(e); return null; }
    }, [adminFetch]);

    const resetLocalState = () => {
        try {
            localStorage.removeItem('factory_v3_state_v1');
            localStorage.removeItem('factory_v3_recent_subs'); // Cleanup legacy
            window.location.reload();
        } catch (e) { alert("Reset failed: " + e); }
    };

    // New Helper: Fetch Manifest Details (Lazy/On-Demand)
    const fetchManifest = useCallback(async (id: string) => {
        try {
            // BUG FIX: Cache Busting to ensure we get fresh trimStartSeconds after update
            const r = await adminFetch(`/api/library/audio_audit?id=${id}&ts=${Date.now()}`);
            const d = await r.json();
            // d should be the audit details.
            // If it contains the manifest entry (usually `entry` or `manifest`), we use it.
            // If not, we might fail to get it without a worker change.
            // However, assuming `trimStartSeconds` is in the returned audit object (often merged).

            // IF that fails, we can fall back to the idea that `audit` (the full list) might have it if we re-run it? No.
            // Let's assume `d.trimStartSeconds` or `d.entry.trimStartSeconds` exists.

            // Patch Local State
            if (d.ok) {
                // Heuristic: check root or entry
                const val = d.entry?.trimStartSeconds ?? d.trimStartSeconds;
                if (val !== undefined) {
                    setData(prev => (prev || []).map(b => b.id === id ? { ...b, trimStartSeconds: val } : b));
                    return val;
                }
            }
            return null;
        } catch (e) { console.error("Manifest fetch failed", e); return null; }
    }, [adminFetch]);

    const updateManifest = async (id: string, updates: any) => {
        const r = await adminFetch(`/api/library/manifest/${id}/update`, {
            method: 'POST',
            body: JSON.stringify(updates)
        });
        const d = await r.json();
        if (d.ok) {
            // Update local state immediately
            if (updates.trimStartSeconds !== undefined) {
                setData(prev => (prev || []).map(b => b.id === id ? { ...b, trimStartSeconds: updates.trimStartSeconds } : b));
            }
        }
        return d;
    };

    return {
        data, activeJobs, diagnostics, meta, dlqEvents, loading: isAuthLoading, queueLoading, error, refresh,
        repairStep: repairStepFull, runDrain, abandonStale, dumpQueue,
        fetchDlqRecent, requeueDlq, abandonDlq, regenerateCover, rebuildThumbs, reconcileBook,
        kickJob, setJobParam, runRepairCook, getJobStatus, inspectBook, resetLocalState, auditFailed, auditError, auditLoading,
        fetchManifest, updateManifest
    };
};

